/**
 * GlobalView — MobileBottomNav (Spy Cockpit HUD)
 *
 * Bottom navigation bar for mobile viewports (≤ 768px).
 * Renders 5 HUD-style tabs with slide-up sheet panels.
 *
 * Tabs: LAYERS, FEEDS, SEARCH, INFO, USER
 *
 * Design:
 *  - Glassmorphism bar with cyan glow border
 *  - HUD bracket corners
 *  - Active tab cyan accent + glow bar
 *  - Sheet panels slide up from bottom
 */

import { useState, useCallback } from 'react';
import { useEntityStore, selectAircraftCount, selectTrainCount, selectSatelliteCount, selectShipCount, selectEarthquakeCount, selectGlobalEarthquakeCount, selectSwissEarthquakeCount } from '../../stores/entityStore';
import { useGlobeStore } from '../../stores/globeStore';
import { useAuthStore } from '../../stores/authStore';
import { connect as wsConnect, disconnect as wsDisconnect, isConnected } from '../../services/wsClient';
import StatusBadge from '../ui/StatusBadge';
import styles from './MobileBottomNav.module.css';

type SheetId = 'layers' | 'feeds' | 'search' | 'info' | 'user' | null;

interface Tab {
  id: SheetId & string;
  icon: string;
  label: string;
}

const TABS: Tab[] = [
  { id: 'layers', icon: '◉', label: 'LAYERS' },
  { id: 'feeds',  icon: '◎', label: 'FEEDS' },
  { id: 'search', icon: '⌕', label: 'SEARCH' },
  { id: 'info',   icon: '◇', label: 'INFO' },
  { id: 'user',   icon: '⦿', label: 'USER' },
];

export default function MobileBottomNav({ onShowAdmin }: { onShowAdmin?: () => void }) {
  const [activeSheet, setActiveSheet] = useState<SheetId>(null);

  const toggleSheet = useCallback((id: SheetId) => {
    setActiveSheet((prev) => (prev === id ? null : id));
  }, []);

  const closeSheet = useCallback(() => {
    setActiveSheet(null);
  }, []);

  return (
    <div className={styles.wrapper} id="mobile-bottom-nav">
      {/* Backdrop dimmer */}
      {activeSheet && (
        <div
          className={styles.backdrop}
          onClick={closeSheet}
          id="mobile-sheet-backdrop"
        />
      )}

      {/* Sheet panel */}
      {activeSheet && (
        <div className={styles.sheet} id={`mobile-sheet-${activeSheet}`}>
          <SheetContent sheetId={activeSheet} onClose={closeSheet} onShowAdmin={onShowAdmin} />
        </div>
      )}

      {/* Navigation bar */}
      <nav className={styles.navBar} id="mobile-nav-bar">
        {/* HUD bracket corners */}
        <div className={styles.hudBracketLeft} />
        <div className={styles.hudBracketRight} />

        {TABS.map((tab) => (
          <button
            key={tab.id}
            className={`${styles.tab} ${activeSheet === tab.id ? styles.active : ''}`}
            onClick={() => toggleSheet(tab.id)}
            id={`mobile-tab-${tab.id}`}
          >
            <span className={styles.tabIcon}>{tab.icon}</span>
            <span className={styles.tabLabel}>{tab.label}</span>
          </button>
        ))}
      </nav>
    </div>
  );
}

/* ─── Sheet Content Router ──────────────────────────── */

function SheetContent({ sheetId, onClose, onShowAdmin }: { sheetId: SheetId; onClose: () => void; onShowAdmin?: () => void }) {
  const title = TABS.find((t) => t.id === sheetId)?.label ?? '';

  return (
    <>
      <div className={styles.sheetHeader}>
        <span className={styles.sheetTitle}>{title}</span>
        <button className={styles.sheetClose} onClick={onClose} id="mobile-sheet-close">
          ✕
        </button>
      </div>
      <div className={styles.sheetBody}>
        {sheetId === 'layers' && <LayersSheet />}
        {sheetId === 'feeds' && <PlaceholderSheet text="Kamera- und Sensorquellen" phase="Phase 3+" />}
        {sheetId === 'search' && <SearchSheet />}
        {sheetId === 'info' && <InfoSheet />}
        {sheetId === 'user' && <UserSheet onClose={onClose} onShowAdmin={onShowAdmin} />}
      </div>
    </>
  );
}

/* ─── Layers Sheet ──────────────────────────────────── */

function LayersSheet() {
  const aircraftCount = useEntityStore(selectAircraftCount);
  const trainCount = useEntityStore(selectTrainCount);
  const satelliteCount = useEntityStore(selectSatelliteCount);
  const wsConnected = useEntityStore((s) => s.wsConnected);
  const airLayerVisible = useGlobeStore((s) => s.airLayerVisible);
  const airLabelsVisible = useGlobeStore((s) => s.airLabelsVisible);
  const railLayerVisible = useGlobeStore((s) => s.railLayerVisible);
  const railLabelsVisible = useGlobeStore((s) => s.railLabelsVisible);
  const spaceLayerVisible = useGlobeStore((s) => s.spaceLayerVisible);
  const spaceLabelsVisible = useGlobeStore((s) => s.spaceLabelsVisible);
  const starlinkVisible = useGlobeStore((s) => s.starlinkVisible);
  const toggleAirLayer = useGlobeStore((s) => s.toggleAirLayer);
  const toggleAirLabels = useGlobeStore((s) => s.toggleAirLabels);
  const toggleRailLayer = useGlobeStore((s) => s.toggleRailLayer);
  const toggleRailLabels = useGlobeStore((s) => s.toggleRailLabels);
  const toggleSpaceLayer = useGlobeStore((s) => s.toggleSpaceLayer);
  const toggleSpaceLabels = useGlobeStore((s) => s.toggleSpaceLabels);
  const toggleStarlink = useGlobeStore((s) => s.toggleStarlink);
  const seaLayerVisible = useGlobeStore((s) => s.seaLayerVisible);
  const seaLabelsVisible = useGlobeStore((s) => s.seaLabelsVisible);
  const toggleSeaLayer = useGlobeStore((s) => s.toggleSeaLayer);
  const toggleSeaLabels = useGlobeStore((s) => s.toggleSeaLabels);
  const shipCount = useEntityStore(selectShipCount);
  const quakeLayerVisible = useGlobeStore((s) => s.quakeLayerVisible);
  const toggleQuakeLayer = useGlobeStore((s) => s.toggleQuakeLayer);
  const earthquakeCount = useEntityStore(selectEarthquakeCount);
  const quakeGlobalVisible = useGlobeStore((s) => s.quakeGlobalVisible);
  const toggleQuakeGlobal = useGlobeStore((s) => s.toggleQuakeGlobal);
  const globalEarthquakeCount = useEntityStore(selectGlobalEarthquakeCount);
  const quakeSwissVisible = useGlobeStore((s) => s.quakeSwissVisible);
  const toggleQuakeSwiss = useGlobeStore((s) => s.toggleQuakeSwiss);
  const swissEarthquakeCount = useEntityStore(selectSwissEarthquakeCount);

  const categories = [
    { name: 'Base', color: 'var(--text-dim)', enabled: true, count: undefined, toggleable: false },
    { name: 'Air', color: 'var(--accent)', enabled: airLayerVisible && wsConnected, count: airLayerVisible ? aircraftCount : undefined, toggleable: true },
    { name: 'Rail', color: '#4a9a2f', enabled: railLayerVisible && wsConnected, count: railLayerVisible ? trainCount : undefined, toggleable: true },
    { name: 'Space', color: '#FFD700', enabled: spaceLayerVisible && wsConnected, count: spaceLayerVisible ? satelliteCount : undefined, toggleable: true },
    { name: 'Maritime', color: 'var(--accent4)', enabled: seaLayerVisible && wsConnected, count: seaLayerVisible ? shipCount : undefined, toggleable: true },
    { name: 'Ground', color: 'var(--accent5)', enabled: false, count: undefined, toggleable: false },
    { name: 'EW / Signal', color: 'var(--accent3)', enabled: false, count: undefined, toggleable: false },
    { name: 'Earthquake', color: '#ff4444', enabled: quakeLayerVisible, count: quakeLayerVisible ? earthquakeCount : undefined, toggleable: true },
  ];

  /** Check if any live layer will remain active */
  const anyLayerActive = (excludeLayer?: string) => {
    const layers = [
      { name: 'Air', visible: airLayerVisible },
      { name: 'Rail', visible: railLayerVisible },
      { name: 'Space', visible: spaceLayerVisible },
      { name: 'Maritime', visible: seaLayerVisible },
    ];
    return layers.some((l) => l.name !== excludeLayer && l.visible);
  };

  const handleToggle = (cat: { name: string; toggleable: boolean }) => {
    if (!cat.toggleable) return;
    if (cat.name === 'Air') {
      toggleAirLayer();
      const nextVisible = !airLayerVisible;
      if (nextVisible || anyLayerActive('Air')) {
        if (!isConnected()) wsConnect();
      } else {
        wsDisconnect();
      }
    }
    if (cat.name === 'Rail') {
      toggleRailLayer();
      const nextVisible = !railLayerVisible;
      if (nextVisible || anyLayerActive('Rail')) {
        if (!isConnected()) wsConnect();
      } else {
        wsDisconnect();
      }
    }
    if (cat.name === 'Space') {
      toggleSpaceLayer();
      const nextVisible = !spaceLayerVisible;
      if (nextVisible || anyLayerActive('Space')) {
        if (!isConnected()) wsConnect();
      } else {
        wsDisconnect();
      }
    }
    if (cat.name === 'Maritime') {
      toggleSeaLayer();
      const nextVisible = !seaLayerVisible;
      if (nextVisible || anyLayerActive('Maritime')) {
        if (!isConnected()) wsConnect();
      } else {
        wsDisconnect();
      }
    }
    if (cat.name === 'Earthquake') {
      toggleQuakeLayer();
    }
  };

  return (
    <>
      {categories.map((cat) => (
        <div key={cat.name}>
          <div
            className={styles.sheetLayerItem}
            onClick={() => handleToggle(cat)}
          >
            <span
              className={styles.sheetLayerDot}
              style={{ background: cat.color, opacity: cat.enabled ? 1 : 0.3 }}
            />
            <span className={styles.sheetLayerName}>{cat.name}</span>
            <span className={styles.sheetLayerStatus}>
              {cat.count !== undefined ? `${cat.count}` : cat.enabled ? 'ON' : 'OFF'}
            </span>
          </div>

          {/* Sub-options for Air */}
          {cat.name === 'Air' && airLayerVisible && (
            <div className={styles.sheetSubItem} onClick={toggleAirLabels}>
              <span className={styles.sheetSubLabel}>Labels</span>
              <span className={styles.sheetSubToggle}>{airLabelsVisible ? 'ON' : 'OFF'}</span>
            </div>
          )}

          {/* Sub-options for Rail */}
          {cat.name === 'Rail' && railLayerVisible && (
            <div className={styles.sheetSubItem} onClick={toggleRailLabels}>
              <span className={styles.sheetSubLabel}>Labels</span>
              <span className={styles.sheetSubToggle}>{railLabelsVisible ? 'ON' : 'OFF'}</span>
            </div>
          )}

          {/* Sub-options for Space */}
          {cat.name === 'Space' && spaceLayerVisible && (
            <>
              <div className={styles.sheetSubItem} onClick={toggleSpaceLabels}>
                <span className={styles.sheetSubLabel}>Labels</span>
                <span className={styles.sheetSubToggle}>{spaceLabelsVisible ? 'ON' : 'OFF'}</span>
              </div>
              <div className={styles.sheetSubItem} onClick={toggleStarlink}>
                <span className={styles.sheetSubLabel}>Starlink</span>
                <span className={styles.sheetSubToggle}>{starlinkVisible ? 'ON' : 'OFF'}</span>
              </div>
            </>
          )}

          {/* Sub-options for Maritime */}
          {cat.name === 'Maritime' && seaLayerVisible && (
            <div className={styles.sheetSubItem} onClick={toggleSeaLabels}>
              <span className={styles.sheetSubLabel}>Labels</span>
              <span className={styles.sheetSubToggle}>{seaLabelsVisible ? 'ON' : 'OFF'}</span>
            </div>
          )}

          {/* Sub-options for Earthquake */}
          {cat.name === 'Earthquake' && quakeLayerVisible && (
            <>
              <div className={styles.sheetSubItem} onClick={toggleQuakeGlobal}>
                <span className={styles.sheetSubLabel}>Erdbeben Global</span>
                <span className={styles.sheetSubToggle}>
                  {quakeGlobalVisible ? (globalEarthquakeCount > 0 ? `${globalEarthquakeCount}` : 'ON') : 'OFF'}
                </span>
              </div>
              <div className={styles.sheetSubItem} onClick={toggleQuakeSwiss}>
                <span className={styles.sheetSubLabel}>Erdbeben Schweiz</span>
                <span className={styles.sheetSubToggle}>
                  {quakeSwissVisible ? (swissEarthquakeCount > 0 ? `${swissEarthquakeCount}` : 'ON') : 'OFF'}
                </span>
              </div>
            </>
          )}
        </div>
      ))}
    </>
  );
}

/* ─── Info Sheet (ContextPanel data) ────────────────── */

function InfoSheet() {
  const cursorPosition = useGlobeStore((s) => s.cursorPosition);
  const cameraPosition = useGlobeStore((s) => s.cameraPosition);
  const viewMode = useGlobeStore((s) => s.viewMode);
  const ready = useGlobeStore((s) => s.ready);
  const tileMode = useGlobeStore((s) => s.tileMode);
  const selectedEntityId = useGlobeStore((s) => s.selectedEntityId);
  const entity = useEntityStore((s) =>
    selectedEntityId ? s.entities.get(selectedEntityId) : undefined,
  );

  const fmt = (val: number, decimals = 5) => val.toFixed(decimals);
  const fmtAlt = (meters: number) => {
    if (meters >= 1_000_000) return `${(meters / 1_000_000).toFixed(1)} Mm`;
    if (meters >= 1_000) return `${(meters / 1_000).toFixed(1)} km`;
    return `${Math.round(meters)} m`;
  };

  const props = entity?.properties as Record<string, unknown> | undefined;
  const callsign = props?.callsign as string | undefined;

  return (
    <>
      {/* Selection */}
      <div className={styles.infoSectionLabel}>SELECTION</div>
      {selectedEntityId ? (
        <div className={styles.infoRow}>
          <span className={styles.infoLabel}>ID</span>
          <span className={styles.infoValueAccent}>{callsign || selectedEntityId}</span>
        </div>
      ) : (
        <div className={styles.infoRow}>
          <span className={styles.infoLabel} />
          <span className={styles.infoValue} style={{ fontStyle: 'italic' }}>Kein Objekt</span>
        </div>
      )}

      <div className={styles.infoDivider} />

      {/* Camera */}
      <div className={styles.infoSectionLabel}>KAMERA</div>
      {ready ? (
        <>
          <div className={styles.infoRow}>
            <span className={styles.infoLabel}>LAT</span>
            <span className={styles.infoValue}>{fmt(cameraPosition.lat)}</span>
          </div>
          <div className={styles.infoRow}>
            <span className={styles.infoLabel}>LON</span>
            <span className={styles.infoValue}>{fmt(cameraPosition.lon)}</span>
          </div>
          <div className={styles.infoRow}>
            <span className={styles.infoLabel}>ALT</span>
            <span className={styles.infoValue}>{fmtAlt(cameraPosition.alt)}</span>
          </div>
        </>
      ) : (
        <div className={styles.infoRow}>
          <span className={styles.infoLabel} />
          <span className={styles.infoValue}>—</span>
        </div>
      )}

      <div className={styles.infoDivider} />

      {/* Cursor */}
      <div className={styles.infoSectionLabel}>CURSOR</div>
      {cursorPosition ? (
        <>
          <div className={styles.infoRow}>
            <span className={styles.infoLabel}>LAT</span>
            <span className={styles.infoValue}>{fmt(cursorPosition.lat)}</span>
          </div>
          <div className={styles.infoRow}>
            <span className={styles.infoLabel}>LON</span>
            <span className={styles.infoValue}>{fmt(cursorPosition.lon)}</span>
          </div>
        </>
      ) : (
        <div className={styles.infoRow}>
          <span className={styles.infoLabel} />
          <span className={styles.infoValue} style={{ fontStyle: 'italic' }}>Touch auf Globe</span>
        </div>
      )}

      <div className={styles.infoDivider} />

      {/* Status */}
      <div className={styles.infoSectionLabel}>STATUS</div>
      <div className={styles.infoRow}>
        <span className={styles.infoLabel}>MODE</span>
        <StatusBadge variant="online" label="LIVE" pulse />
      </div>
      <div className={styles.infoRow}>
        <span className={styles.infoLabel}>VIEW</span>
        <span className={styles.infoValueAccent}>{viewMode.toUpperCase()}</span>
      </div>
      <div className={styles.infoRow}>
        <span className={styles.infoLabel}>3D</span>
        <span className={styles.infoValueAccent}>{tileMode === 'google3d' ? 'GOOGLE' : 'STD'}</span>
      </div>
    </>
  );
}

/* ─── Search Sheet ──────────────────────────────────── */

function SearchSheet() {
  return (
    <div className={styles.searchOverlay}>
      <svg className={styles.searchIcon} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="11" cy="11" r="8" />
        <path d="m21 21-4.3-4.3" />
      </svg>
      <input
        type="text"
        className={styles.searchInput}
        placeholder="Orte, Koordinaten, Objekte…"
        autoFocus
        id="mobile-search-input"
      />
    </div>
  );
}

/* ─── User Sheet ────────────────────────────────────── */

function UserSheet({ onClose, onShowAdmin }: { onClose: () => void; onShowAdmin?: () => void }) {
  const user = useAuthStore((s) => s.user);
  const role = useAuthStore((s) => s.role);
  const signOut = useAuthStore((s) => s.signOut);

  return (
    <>
      <div className={styles.userSheetInfo}>
        <span className={styles.userSheetName}>{user?.displayName || 'User'}</span>
        <span className={styles.userSheetEmail}>{user?.email}</span>
        <span className={styles.userSheetRole}>{role?.toUpperCase()}</span>
      </div>

      {onShowAdmin && (
        <button
          className={styles.userSheetButton}
          onClick={() => { onShowAdmin(); onClose(); }}
          id="mobile-admin-link"
        >
          Admin-Panel
        </button>
      )}
      <button
        className={styles.userSheetButton}
        onClick={signOut}
        id="mobile-sign-out"
      >
        Abmelden
      </button>
    </>
  );
}

/* ─── Placeholder Sheet ─────────────────────────────── */

function PlaceholderSheet({ text, phase }: { text: string; phase: string }) {
  return (
    <div className={styles.sheetPlaceholder}>
      <span className={styles.sheetPlaceholderText}>{text}</span>
      <span className={styles.sheetPlaceholderPhase}>{phase}</span>
    </div>
  );
}
